import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  AvatarComponent,
  MyAccountChanges,
  AboutMeComponent,
  UpdateFavTeam,
} from "../Components";
import { motion } from "framer-motion";

function UpdateMyAccount() {
  const navigate = useNavigate();
  const [loggedUser, setLoggedUser] = useState({});
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [userDetails, setUserDetails] = useState({
    username: "",
    email: "",
    password: "",
    about_me: "",
    favorite_team: "",
    phone_number:"",
    sms_bet_notifications: false,
    avatar: "",
  });

  useEffect(() => {
    const fetchUserData = async () => {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError) {
        console.error("Error fetching session:", sessionError);
        return;
      }

      if (sessionData && sessionData.session) {
        const user = sessionData.session.user;
        const { data: userData, error: userError } = await supabase
          .from("users")
          .select("*")
          .eq("public_user_id", user.id)
          .single();

        if (userError) {
          console.error("Error fetching user data:", userError);
        } else {
          setLoggedUser(userData);
          setUserDetails({
            username: userData.username || "",
            email: userData.email || "",
            password: "",
            about_me: userData.about_me || "",
            favorite_team: userData.favorite_team || "",
            phone_number: userData.phone_number || "",
            sms_bet_notifications: Boolean(userData.sms_bet_notifications),
            avatar: userData.avatar || "",
          });
        }
      }
    };

    fetchUserData();
  }, []);

  const handleUserDetailChange = (field, value) => {
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      [field]: value,
    }));
  };

  const handleAvatarFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !loggedUser.public_user_id) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      event.target.value = "";
      return;
    }

    setAvatarUploading(true);

    try {
      const extension = file.name.split(".").pop()?.toLowerCase() || "png";
      const safeExtension = extension.replace(/[^a-z0-9]/g, "") || "png";
      const filePath = `${loggedUser.public_user_id}/avatar-${Date.now()}.${safeExtension}`;

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, {
          cacheControl: "3600",
          // Use insert-only uploads so bucket only needs INSERT policy.
          upsert: false,
        });

      if (uploadError) {
        console.error("Avatar upload failed:", uploadError);
        alert(
          `Avatar upload failed: ${uploadError.message || "Unknown error"}. ` +
          "Ensure the 'avatars' bucket exists and your Storage policy allows INSERT for your folder."
        );
        return;
      }

      const { data: publicData } = supabase.storage
        .from("avatars")
        .getPublicUrl(filePath);

      if (!publicData?.publicUrl) {
        alert("Avatar uploaded, but public URL could not be generated.");
        return;
      }

      setUserDetails((prev) => ({
        ...prev,
        avatar: publicData.publicUrl,
      }));

      alert("Avatar uploaded successfully.");
    } catch (error) {
      console.error("Avatar upload error:", error);
      alert("There was an unexpected error uploading your avatar.");
    } finally {
      setAvatarUploading(false);
      event.target.value = "";
    }
  };

  const handleUpdateAll = async () => {
    const updatedDetails = {
      username: userDetails.username || loggedUser.username,
      email: userDetails.email || loggedUser.email,
      password: userDetails.password,
      about_me: userDetails.about_me || loggedUser.about_me,
      favorite_team: userDetails.favorite_team || loggedUser.favorite_team,
      phone_number: userDetails.phone_number || loggedUser.phone_number,
      sms_bet_notifications: userDetails.sms_bet_notifications,
      avatar: userDetails.avatar || loggedUser.avatar,
    };

    // Update Supabase user details
    const { error: updateUserError } = await supabase
      .from("users")
      .update({
        username: updatedDetails.username,
        email: updatedDetails.email,
        about_me: updatedDetails.about_me,
        favorite_team: updatedDetails.favorite_team,
        phone_number: updatedDetails.phone_number,
        sms_bet_notifications: updatedDetails.sms_bet_notifications,
        avatar: updatedDetails.avatar,
      })
      .eq("public_user_id", loggedUser.public_user_id);

    if (updateUserError) {
      console.error("Error updating user details:", updateUserError);
      alert("There was an error updating your details. Please try again.");
      return;
    }

    // Update password if it was changed
    if (updatedDetails.password) {
      const { error: updatePasswordError } = await supabase.auth.updateUser({
        password: updatedDetails.password,
      });

      if (updatePasswordError) {
        console.error("Error updating password:", updatePasswordError);
        alert("There was an error updating your password. Please try again.");
        return;
      }
    }

    // Fetch the updated user data
    const { data: updatedUser, error: fetchUserError } = await supabase
      .from("users")
      .select("*")
      .eq("public_user_id", loggedUser.public_user_id)
      .single();

    if (fetchUserError) {
      console.error("Error fetching updated user data:", fetchUserError);
    } else {
      setLoggedUser(updatedUser);
      alert("Your account has been updated successfully!");
    }

    // Navigate to MyAccount page
    navigate("/MyAccount");
  };

  return (
    <>
      <motion.div
        className="account-page fb-card"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28, ease: "easeOut" }}
      >
        <AvatarComponent user={userDetails} />
        <MyAccountChanges
          userDetails={userDetails}
          onUserDetailChange={handleUserDetailChange}
          onAvatarFileSelect={handleAvatarFileSelect}
          avatarUploading={avatarUploading}
        />
        <UpdateFavTeam
          userDetails={userDetails}
          onUserDetailChange={handleUserDetailChange}
        />
        <AboutMeComponent
          userDetails={userDetails}
          onUserDetailChange={handleUserDetailChange}
        />
        <button
          onClick={handleUpdateAll}
          className="fb-btn fb-btn--primary account-save-btn"
        >
          Save Changes
        </button>
      </motion.div>
    </>
  );
}

export { UpdateMyAccount };
